import type { ApiErrorCode } from '../contracts/types.js';

/**
 * A typed API failure. Mirrors the contract's `apiErrorSchema` envelope so callers can
 * switch on `code` exactly as the mobile app does. `requestId` is surfaced to the
 * operator for support correlation.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly requestId: string | null;
  readonly retryAfterSeconds: number | null;

  constructor(args: {
    code: ApiErrorCode;
    message: string;
    status: number;
    requestId?: string | null;
    retryAfterSeconds?: number | null;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.code = args.code;
    this.status = args.status;
    this.requestId = args.requestId ?? null;
    this.retryAfterSeconds = args.retryAfterSeconds ?? null;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** A short, human message for an unexpected/network failure. */
export function messageOf(err: unknown): string {
  if (isApiError(err)) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Try again.';
}
